import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Movies from './Movies';
import MovieLoading from './utils/Loading';
import TagList from './TagList';
import SearchBar from './SearchBar';
import Sidebar from './Sidebar';
import { fetchData } from '../api';
import './Result.css';

const BATCH_SIZE = 50;
const DISPLAY_STEP = 16;

const Result = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allMovies, setAllMovies] = useState([]);
  const [displayedCount, setDisplayedCount] = useState(DISPLAY_STEP);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  // Get tags from URL
  const tagsParam = searchParams.get('tags') || '';
  const selectedTags = useMemo(() => tagsParam ? tagsParam.split(',').filter(t => t.trim()) : [], [tagsParam]);

  // Fetch a batch of movies
  const fetchBatch = useCallback(async (offset, tags) => {
    try {
      const params = new URLSearchParams();
      if (tags.length) params.append('tags', tags.join(','));
      params.append('offset', offset);
      params.append('limit', BATCH_SIZE);
      const { data } = await fetchData(`/api/movie/?${params.toString()}`);
      return data;
    } catch (err) {
      throw new Error('Failed to fetch movies');
    }
  }, []);

  // Load initial batch when tags change
  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      setError(null);
      setAllMovies([]);
      setDisplayedCount(DISPLAY_STEP);
      setOffset(0);
      setHasMore(true);
      try {
        const movies = await fetchBatch(0, selectedTags);
        setAllMovies(movies);
        setHasMore(movies.length === BATCH_SIZE);
        setOffset(movies.length);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, [fetchBatch, selectedTags]); // refetch when tags change

  // Load more movies (either from cache or from API)
  const loadMore = useCallback(async () => {
    // If we have more cached movies, just increase displayed count
    if (Math.min(displayedCount + DISPLAY_STEP, allMovies.length) <= allMovies.length) {
      setDisplayedCount(prev => prev + DISPLAY_STEP);
      return;
    }
    // Otherwise, need to fetch next batch
    if (!hasMore) return; // no more on server

    setLoading(true);
    try {
      const newMovies = await fetchBatch(offset, selectedTags);
      if (newMovies.length === 0) {
        setHasMore(false);
      } else {
        setAllMovies(prev => [...prev, ...newMovies]);
        setOffset(prev => prev + newMovies.length);
        setHasMore(newMovies.length === BATCH_SIZE);
        // After adding new movies, we can now show up to displayedCount + DISPLAY_STEP
        setDisplayedCount(prev => prev + DISPLAY_STEP);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [allMovies.length, displayedCount, fetchBatch, hasMore, offset, selectedTags]);

  const handleTagClick = (tagName) => {
    setSearchParams({ tags: [tagName] });
  };

  // Compute tag cloud from all fetched movies
  const tagCloud = {};
  const tagCount = {};

  allMovies.forEach(movie => {
    movie.tags.forEach(tag => {
      if (!tagCloud[tag.name]) {
          tagCount[tag.name] = 0;
      }
      tagCount[tag.name] += 1;
    })
  });

  const MAX_TAGS = 25;
  const topTags = new Set(
    Object.entries(
        tagCount
    ).sort(
        (a, b) => b[1] - a[1]
    ).slice(0, 10).map(([name]) => name)
 );

  allMovies.forEach(movie => {
    if (movie.tags && Array.isArray(movie.tags)) {
      movie.tags.forEach(tag => {
        if (!["metadata", "staff"].includes(tag.category) && topTags.has(tag.name)) {
            if (!tagCloud[tag.category]) {
                tagCloud[tag.category] = new Set();
            }
            tagCloud[tag.category].add(tag.name);
        }
      });
    }
  });

  const groupedTags = Object.entries(tagCloud).map(([category, names]) => ({
    category,
    names: Array.from(names)
  }));

  if (loading && allMovies.length === 0) return <MovieLoading />;
  if (error) return <div className="error">Error: {error}</div>;

  const moviesToShow = allMovies.slice(0, displayedCount);
  const moreAvailable = displayedCount < allMovies.length || hasMore;

  return (
    <div className="recommendations-container">
      {/* Main two-column layout */}
      <div className="recommendations-layout">
        {/* Sidebar */}
        <Sidebar allMovies={allMovies}/>
        {/* Main content */}
        <main className="recommendations-main">
          <div className="recommendations-header">
            <h2 className="recommendations-title">Your Recommended Movies</h2>
            <p className="recommendations-subtitle">
              We found {allMovies.length} movie{allMovies.length !== 1 ? 's' : ''} matching your taste
            </p>
          </div>

          <Movies
            movies={moviesToShow}
            onLoadMore={loadMore}
            hasMore={moreAvailable}
            isLoading={loading}
            total={Math.min(offset + BATCH_SIZE, allMovies.length)}
          />
        </main>
      </div>
    </div>
  );
};

export default Result;
